import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import type { TranslationKey } from './i18n';

/**
 * Calls from the portal (operator, 2026-11-01: "add call to client portal let client call selected
 * employees we will select from client which employee can call also all call will have same calls
 * features"). A client calls the employees its form in the admin lets it call — the same calls as our
 * people's: recorded before anyone speaks, one mixed file of the whole call, kept by Viganium. The API
 * decides everything a call is; LiveKit carries the audio. The client reads its own calls, never their
 * recording.
 */

export type CallType = 'ONE_TO_ONE' | 'GROUP';
export type CallStatus =
  | 'RINGING'
  | 'PREPARING'
  | 'ACTIVE'
  | 'ENDING'
  | 'COMPLETED'
  | 'MISSED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'BUSY'
  | 'FAILED';
export type ParticipantStatus = 'RINGING' | 'ACCEPTED' | 'DECLINED' | 'MISSED' | 'BUSY' | 'CANCELLED' | 'LEFT';
/** How a call went for the client, its caller. */
export type CallOutcome = 'LIVE' | 'ANSWERED' | 'NO_ANSWER' | 'MISSED' | 'DECLINED' | 'BUSY' | 'CANCELLED' | 'FAILED';

export interface CallPerson {
  id: string;
  fullName: string;
  fullNameAr: string | null;
  jobTitle: string | null;
  /** One of Viganium's people, or one of the client's sign-ins. */
  kind?: 'EMPLOYEE' | 'CLIENT';
}

export interface CallParticipant {
  user: CallPerson;
  isCaller: boolean;
  status: ParticipantStatus;
  invitedAt: string;
  answeredAt: string | null;
  joinedAt: string | null;
  leftAt: string | null;
  inRoom: boolean;
  leftReason: string | null;
}

/** `current` of `GET /portal/calls/live`: the call this sign-in is in. */
export interface LiveCall {
  id: string;
  type: CallType;
  status: CallStatus;
  createdAt: string;
  startedAt: string | null;
  endsBy: string | null;
  endingSoon: boolean;
  caller: CallPerson | null;
  participants: CallParticipant[];
  recorded: true;
  failureCode: string | null;
  me: {
    status: ParticipantStatus;
    isCaller: boolean;
    /** This tab is the one that called. Only it joins the room. */
    thisDevice: boolean;
    /** The recorder is confirmed: the microphone may go on. */
    maySpeak: boolean;
    mayAddPeople: boolean;
  };
}

export interface LiveState {
  serverTime: string;
  current: LiveCall | null;
}

/** Someone the client may call: a name, a job title, and whether they are free now. */
export interface CallablePerson {
  id: string;
  fullName: string;
  fullNameAr: string | null;
  jobTitle: string | null;
  /** On shift now. */
  working: boolean;
  onBreak: boolean;
  /** On another call now: a call to them comes back busy. */
  onCall: boolean;
}

/** Which of the client's people made a call: `you`, a contact by name, or the company's own sign-in (no name). */
export interface CallMadeBy {
  you: boolean;
  name: string | null;
  jobTitle: string | null;
}

export interface ClientCallSummary {
  id: string;
  type: CallType;
  status: CallStatus;
  outcome: CallOutcome;
  createdAt: string;
  startedAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  madeBy: CallMadeBy | null;
  people: Array<{
    id: string;
    fullName: string;
    fullNameAr: string | null;
    jobTitle: string | null;
    status: ParticipantStatus;
    joined: boolean;
  }>;
  recorded: true;
}

export interface ClientCallDetail extends ClientCallSummary {
  endedReason: string | null;
  failureCode: string | null;
}

export interface ClientCallPage {
  items: ClientCallSummary[];
  nextCursor: string | null;
}

export interface ClientCallsStatus {
  available: boolean;
  recorded: true;
  settings: { ringTimeoutSeconds: number; maxParticipants: number; maxDurationMinutes: number };
}

// ---------------------------------------------------------------------------
// This tab
// ---------------------------------------------------------------------------

const DEVICE_KEY = 'viganium.portal.callDevice';

/**
 * This tab's own name for the calls it makes: one per tab, kept while the tab lives (a reload keeps it), so
 * only the tab that called joins the call's room, and another tab of the same sign-in shows it elsewhere.
 */
export function callDeviceId(): string {
  try {
    const kept = sessionStorage.getItem(DEVICE_KEY);
    if (kept && /^[A-Za-z0-9_-]{8,64}$/.test(kept)) return kept;
    const fresh = `portal-${crypto.randomUUID()}`;
    sessionStorage.setItem(DEVICE_KEY, fresh);
    return fresh;
  } catch {
    return `portal-${crypto.randomUUID()}`;
  }
}

// ---------------------------------------------------------------------------
// The API
// ---------------------------------------------------------------------------

export const callsApi = {
  status: () => api.get<ClientCallsStatus>('/calls/status'),
  people: () => api.get<CallablePerson[]>('/calls/people'),
  live: (deviceId: string) => api.get<LiveState>('/calls/live', { deviceId }),
  create: (userIds: string[], deviceId: string) => api.post<LiveCall>('/calls', { userIds, deviceId }),
  invite: (callId: string, userIds: string[]) => api.post<LiveCall>(`/calls/${callId}/invitations`, { userIds }),
  hangUp: (callId: string) => api.post(`/calls/${callId}/hangup`),
  mediaToken: (callId: string, deviceId: string) =>
    api.post<{ url: string; token: string; identity: string; maySpeak: boolean }>(`/calls/${callId}/media-token`, { deviceId }),
  history: (params: { filter?: string; cursor?: string; limit?: number }) => api.get<ClientCallPage>('/calls/history', params),
  detail: (callId: string) => api.get<ClientCallDetail>(`/calls/${callId}`),
};

/** Whether the client can call now, and the settings a call follows. */
export function useCallsStatus() {
  return useQuery({ queryKey: ['calls', 'status'], queryFn: callsApi.status, staleTime: 60_000, refetchInterval: 5 * 60_000 });
}

/** The people the client may call, and whether each is free — looked at again every half minute. */
export function useCallablePeople() {
  return useQuery({ queryKey: ['calls', 'people'], queryFn: callsApi.people, refetchInterval: 30_000 });
}

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

export const OUTCOME_LABEL: Record<CallOutcome, TranslationKey> = {
  LIVE: 'calls.outcome.LIVE',
  ANSWERED: 'calls.outcome.ANSWERED',
  NO_ANSWER: 'calls.outcome.NO_ANSWER',
  MISSED: 'calls.outcome.MISSED',
  DECLINED: 'calls.outcome.DECLINED',
  BUSY: 'calls.outcome.BUSY',
  CANCELLED: 'calls.outcome.CANCELLED',
  FAILED: 'calls.outcome.FAILED',
};

export const OUTCOME_TONE: Record<CallOutcome, 'success' | 'error' | 'warning' | 'neutral' | 'info'> = {
  LIVE: 'info',
  ANSWERED: 'success',
  NO_ANSWER: 'warning',
  MISSED: 'error',
  DECLINED: 'neutral',
  BUSY: 'warning',
  CANCELLED: 'neutral',
  FAILED: 'error',
};

export const PARTICIPANT_LABEL: Record<ParticipantStatus, TranslationKey> = {
  RINGING: 'calls.participant.RINGING',
  ACCEPTED: 'calls.participant.ACCEPTED',
  DECLINED: 'calls.participant.DECLINED',
  MISSED: 'calls.participant.MISSED',
  BUSY: 'calls.participant.BUSY',
  CANCELLED: 'calls.participant.CANCELLED',
  LEFT: 'calls.participant.LEFT',
};

/** Why a call stopped, said plainly — never a provider's words. */
export function endedText(code: string | null | undefined): TranslationKey {
  switch (code) {
    case 'RECORDING_NOT_READY':
    case 'RECORDING_NOT_STARTED':
    case 'RECORDING_STORAGE_UNREACHABLE':
      return 'calls.ended.recordingNotStarted';
    case 'RECORDING_LOST':
    case 'RECORDING_UNCONFIRMED':
      return 'calls.ended.recordingLost';
    case 'MAX_DURATION':
      return 'calls.ended.maxDuration';
    default:
      return 'calls.ended.normal';
  }
}

/** Between names in a list: the Arabic comma in Arabic. */
export function listSeparator(locale: 'en' | 'ar'): string {
  return locale === 'ar' ? '، ' : ', ';
}

/** `m:ss`, or `h:mm:ss` past an hour. */
export function callClock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

// ---------------------------------------------------------------------------
// The ringback, and the microphone
// ---------------------------------------------------------------------------

let ringbackContext: AudioContext | null = null;
let ringbackTimer = 0;

/** The ringback tone: 425 Hz, a second on and four off — what a phone network plays a caller. The admin's, the same. */
export const RINGBACK = { hz: 425, onMs: 1000, everyMs: 5000 } as const;

/**
 * What the client hears while the call rings for our people: the ringback tone, drawn with Web Audio,
 * quiet, until someone answers or the call is called off. Started by the click that called, which is what
 * lets a browser play it.
 */
export function startRingback(): void {
  if (ringbackTimer) return;
  if (typeof AudioContext === 'undefined') return;
  if (navigator.userActivation && !navigator.userActivation.hasBeenActive) return;
  ringbackContext ??= new AudioContext();
  const ctx = ringbackContext;
  const tone = () => {
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
    const start = ctx.currentTime + 0.02;
    const end = start + RINGBACK.onMs / 1000;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = RINGBACK.hz;
    // Faded in and out, so it never clicks.
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.1, start + 0.04);
    gain.gain.setValueAtTime(0.1, end - 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);
    osc.connect(gain).connect(ctx.destination);
    osc.start(start);
    osc.stop(end + 0.01);
  };
  tone();
  ringbackTimer = window.setInterval(tone, RINGBACK.everyMs);
}

/** Answered, declined, missed or called off: the ringback stops at once — a tone half played included. */
export function stopRingback(): void {
  if (!ringbackTimer) return;
  window.clearInterval(ringbackTimer);
  ringbackTimer = 0;
  void ringbackContext?.suspend().catch(() => undefined);
}

/**
 * Asks for the microphone inside the click that calls, so the browser's question comes then — not in the
 * middle of the call, when the recorder is confirmed and the microphone is turned on. The stream is let go
 * at once: nothing is sent before the call is live.
 */
export async function askForMicrophone(): Promise<'granted' | 'blocked' | 'missing'> {
  if (!navigator.mediaDevices?.getUserMedia) return 'missing';
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    for (const track of stream.getTracks()) track.stop();
    return 'granted';
  } catch (e) {
    const name = (e as DOMException)?.name;
    return name === 'NotAllowedError' || name === 'SecurityError' ? 'blocked' : 'missing';
  }
}

/** A call not made because this browser will not give the microphone: the client learns why at once. */
export class MicrophoneRefused extends Error {
  constructor(readonly reason: 'blocked' | 'missing') {
    super(`The microphone is ${reason}.`);
    this.name = 'MicrophoneRefused';
  }
}
